"use client";

import { useState, useEffect } from "react";

interface MarketArticleBadge {
  title: string;
  slug: string;
  date: string;
  cover: string | null;
}

// Module-level cache — shared across all component instances
let cachedArticles: Map<string, MarketArticleBadge> | null = null;
let fetchPromise: Promise<void> | null = null;
let lastFetchTime = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

async function fetchMarketArticles(): Promise<Map<string, MarketArticleBadge>> {
  try {
    const res = await fetch("/api/blog/market-articles");
    if (!res.ok) return new Map();
    const data = await res.json();
    if (!data.success || !Array.isArray(data.articles)) return new Map();

    const map = new Map<string, MarketArticleBadge>();
    for (const a of data.articles) {
      // Each article has traders[] with "market:{slug}" entries
      for (const t of a.traders || []) {
        if (t.startsWith("market:")) {
          const marketSlug = t.replace("market:", "");
          // Keep only the most recent article per market
          if (!map.has(marketSlug)) {
            map.set(marketSlug, {
              title: a.title,
              slug: a.slug,
              date: a.published_at || "",
              cover: a.cover_image_url || null,
            });
          }
        }
      }
    }
    return map;
  } catch {
    return new Map();
  }
}

/**
 * Hook that loads all market-related blog articles once and provides
 * a lookup by market/event slug. Shared cache across all instances.
 */
export function useMarketArticles() {
  const [articles, setArticles] = useState<Map<string, MarketArticleBadge>>(
    cachedArticles || new Map()
  );

  useEffect(() => {
    const now = Date.now();
    if (cachedArticles && now - lastFetchTime < CACHE_TTL) {
      setArticles(cachedArticles);
      return;
    }

    if (!fetchPromise) {
      fetchPromise = fetchMarketArticles().then((map) => {
        cachedArticles = map;
        lastFetchTime = Date.now();
        fetchPromise = null;
      });
    }

    fetchPromise.then(() => {
      if (cachedArticles) setArticles(cachedArticles);
    });
  }, []);

  return articles;
}

/**
 * Look up a specific market's article badge.
 * Checks both the slug and parentEventSlug.
 */
export function useMarketArticleBadge(
  slug: string,
  parentEventSlug?: string | null
): MarketArticleBadge | null {
  const articles = useMarketArticles();
  return (
    articles.get(slug.toLowerCase()) ||
    (parentEventSlug ? articles.get(parentEventSlug.toLowerCase()) : null) ||
    null
  );
}
